import React, { useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import BudgetDonationsModal from './BudgetDonationsModal';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { BottomBar } from '../BottomBar';
import {
  BudgeComponentProps,
  BudgetDollarComponent,
  BudgetSubmitReference,
  IN_KIND_DONATIONS_QUESTION_ID,
} from './utils';
import { HelperContainer, CostContainer } from './ProgramCost';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import RelevancyCheckbox, {
  defaultCostValue,
  isCostValid,
} from './RelevancyCheckbox';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';

export const inKindDonationsCheckboxText =
  'In-kind donations are not relevant to my program';

const StyledDiv = styled.div`
  text-align: center;
  .question-text {
    max-width: 590px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 48px;
    margin-bottom: ${SPACERS.medium};
  }
  .body-text {
    max-width: 632px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 29px;
  }
  .donations-field {
    max-width: 244px;
    margin: 0 auto;
  }
  .calculate-donations {
    margin: ${SPACERS.small} auto 0;
  }
`;

const BudgetDonations = React.forwardRef<
  BudgetSubmitReference,
  BudgeComponentProps & BudgetDollarComponent
>(
  (
    {
      reportId,
      guidanceText,
      initialValue,
      hideBottomBar,
      isReportPassed,
      onNext,
      onPrevious,
    },
    forwardRef,
  ) => {
    const [open, setOpen] = useState<boolean>(false);
    const [donations, setDonations] = useState<string>(initialValue);
    const handleNext = () => onNext(donations);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useImperativeHandle(forwardRef, () => ({
      error: () =>
        donations
          ? undefined
          : 'Please enter a valid donation number.',
      handleSubmit: () => JSON.stringify({ currency: donations }),
    }));

    return (
      <>
        <StyledDiv>
          <TrueImpactText variant="heading-page" className="question-text">
            {isReportPassed
              ? 'What was the total value of in-kind donations of your program for this report year?'
              : 'What is the expected total value of in-kind donations for your program for this report year?'}
          </TrueImpactText>
          <ShowGuidanceText
            sectionId="budget"
            questionId={IN_KIND_DONATIONS_QUESTION_ID}
            guidanceText={guidanceText}
          />
          <TrueImpactText variant="body" className="body-text">
            This includes the market value of any goods or services donated to
            support your organization's overhead, including administration,
            fundraising, and special events costs.
          </TrueImpactText>
          <CostContainer>
            <TrueImpactDollarField
              value={donations}
              toFixedDecimal={2}
              disabled={donations === defaultCostValue}
              onChange={setDonations}
              placeholder="Total in-kind donations"
              className="donations-field"
            />
            <RelevancyCheckbox
              value={donations}
              handleValue={setDonations}
              text={inKindDonationsCheckboxText}
            />
          </CostContainer>
          <HelperContainer>
            <TrueImpactText variant="heading-subheading" align="center">
              Want some help calculating in-kind donations?
            </TrueImpactText>
            <TrueImpactText variant="body" align="center">
              You don’t have to use this, but we’ve got a handy calculator just
              for this that will help.
            </TrueImpactText>
            <TrueImpactButton
              disabled={false}
              variant="secondary-large"
              onClick={handleOpen}
              className="calculate-donations"
            >
              Help me calculate this
            </TrueImpactButton>
          </HelperContainer>
        </StyledDiv>
        {!hideBottomBar && (
          <BottomBar
            disabled={false}
            hideBack={false}
            hideNext={!isCostValid(donations)}
            handleBack={onPrevious}
            handleNext={handleNext}
            backTitle="Back"
            nextTitle="Save & Continue"
          />
        )}
        <BudgetDonationsModal
          reportId={reportId}
          open={open}
          title="Calculate in-kind donations"
          setDonations={setDonations}
          handleClose={handleClose}
        />
      </>
    );
  },
);

export default BudgetDonations;
